import React, { useCallback, useState } from 'react';
import { PatternFormat } from 'react-number-format';

import { Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import Avatar from '@sharedCR/common/Avatar';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import MilestoneStatusCellRenderer from 'app/pages/loanPipelines/LoanPipelinesTable/common/LoanPipelinesCellRenderer/MilestoneStatusCellRenderer';
import { getPopperProps } from 'app/shared/components/common/TooltipWrapper/TooltipWrapper';
import {
  AddressIcon,
  EmailIcon,
  HashTagIcon,
  PhoneIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { COLOR_PALETTE, PHONE_NUMBER_FORMAT } from 'constants/ui';
import useModifiedFieldPendingChangeAuthors from 'hooks/useModifiedFieldPendingChangeAuthors';
import { getCleanPhoneNumber } from 'utils/data';

import { DEBOUNCE_DELAY_MS } from './BorrowerInformation.constants';
import {
  avatarStyles,
  borrowerNameStyles,
  borrowerStyles,
  contactItemStyles,
  contactsWrapperStyles,
  getContactItemWrapperStyles,
  loanNumberButtonStyles,
  wrapperStyles,
} from './BorrowerInformation.styles';
import { BorrowerInformationTestIds } from './BorrowerInformation.testIds';

const iconProps = {
  color: COLOR_PALETTE.GRAY[6],
  size: 18,
};

const BorrowerInformation = ({ isLoading, summary }) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const getModifiedFieldAuthors = useModifiedFieldPendingChangeAuthors();

  const {
    loanNumber,
    primaryBorrowerPhoneNumber,
    primaryBorrowerEmail,
    subjectPropertyAddress,
    primaryBorrowerName,
    initials,
    loanProgress,
    modifiedFields,
    modifiedFieldDetails,
  } = summary;

  const onClickLoanNumber = useCallback(e => {
    navigator.clipboard.writeText(e.target.innerText);

    setIsTooltipOpen(true);
    setTimeout(() => setIsTooltipOpen(false), DEBOUNCE_DELAY_MS);
  }, []);

  const contactData = [
    {
      content: (
        <Tooltip
          PopperProps={getPopperProps()}
          disableHoverListener={isTooltipOpen}
          key={isTooltipOpen}
          open={isTooltipOpen || undefined}
          placement="bottom-end"
          title={isTooltipOpen ? 'Copied' : 'Copy'}
        >
          <Button
            disableRipple
            data-testid={BorrowerInformationTestIds.LOAN_NUMBER_BUTTON}
            sx={loanNumberButtonStyles}
            onClick={onClickLoanNumber}
          >
            {loanNumber}
          </Button>
        </Tooltip>
      ),
      icon: <HashTagIcon {...iconProps} />,
      isShrink: false,
    },
    {
      content: (
        <PatternFormat
          data-truncated
          className="truncate"
          data-testid={BorrowerInformationTestIds.PHONE_NUMBER_TEXT}
          displayType="text"
          format={PHONE_NUMBER_FORMAT}
          // eslint-disable-next-line
          renderText={formattedValue => (
            <Typography data-truncated className="truncate" variant="regular14">
              {formattedValue}
            </Typography>
          )}
          value={primaryBorrowerPhoneNumber && getCleanPhoneNumber(primaryBorrowerPhoneNumber)}
        />
      ),
      icon: <PhoneIcon {...iconProps} />,
      fieldName: 'PrimaryBorrowerPhoneNumber',
    },
    {
      value: primaryBorrowerEmail,
      icon: <EmailIcon {...iconProps} />,
      fieldName: 'PrimaryBorrowerEmail',
    },
    {
      value: subjectPropertyAddress,
      icon: <AddressIcon {...iconProps} />,
      fieldName: 'SubjectPropertyAddress',
    },
  ];

  return (
    <Box sx={wrapperStyles}>
      <Box sx={borrowerStyles}>
        {isLoading ? (
          <Skeleton height={40} variant="rounded" width={200} />
        ) : (
          <Box sx={borrowerStyles}>
            <Avatar customStyles={avatarStyles} initials={initials} />
            <PendingChangeWrapper
              isSmall
              authors={getModifiedFieldAuthors(modifiedFieldDetails, 'PrimaryBorrowerFullName')}
              pendingChangesActionType={
                modifiedFields?.includes('PrimaryBorrowerFullName')
                  ? PendingChangesActionType.MODIFIED
                  : undefined
              }
            >
              <Typography
                data-truncated
                className="truncate"
                sx={borrowerNameStyles}
                variant="regular14"
              >
                {primaryBorrowerName}
              </Typography>
            </PendingChangeWrapper>
          </Box>
        )}

        {isLoading ? (
          <Skeleton height={22} variant="rounded" width={80} />
        ) : (
          <MilestoneStatusCellRenderer value={loanProgress?.lastCompleted} />
        )}
      </Box>

      <Box sx={contactsWrapperStyles}>
        {contactData.map(({ value, icon, content, isShrink = true, fieldName }, index) => (
          <Box key={index} sx={getContactItemWrapperStyles(isShrink)}>
            {isLoading ? (
              <Skeleton height={22} variant="rounded" width={120} />
            ) : (
              <Box sx={contactItemStyles}>
                {icon}
                <PendingChangeWrapper
                  isSmall
                  authors={getModifiedFieldAuthors(modifiedFieldDetails, fieldName)}
                  pendingChangesActionType={
                    modifiedFields?.includes(fieldName)
                      ? PendingChangesActionType.MODIFIED
                      : undefined
                  }
                >
                  {content ?? (
                    <Typography data-truncated className="truncate" variant="regular14">
                      {value}
                    </Typography>
                  )}
                </PendingChangeWrapper>
              </Box>
            )}
            {contactData[index + 1] && (
              <Typography className="separator" variant="regular14">
                ·
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

BorrowerInformation.propTypes = {
  ...WithLoadingPropTypes,
  summary: PropTypes.object.isRequired,
};

export default BorrowerInformation;
